// src/pages/received-quotation/detail.tsx
import { Box, Button, Flex, Group, Loader, Modal, Stack, Text } from "@mantine/core"
import { CaretLeftIcon } from "@phosphor-icons/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router"
import useSWR from "swr"
import { ReceivedQuotationApi } from "../../apis"
import { DotPill } from "../../components/delivery/atoms"
import { QuotationDetailView, type QuotationAction } from "../../components/received-quotation/quotation-detail-view"
import classes from "../../components/received-quotation/quotation-detail.module.css"
import { RejectModal } from "../../components/received-quotation/reject-modal"
import { usePermission } from "../../components/permission"
import { ConfirmDialogBody, DeniedCard, PageHeader } from "../../components/ui"
import { ISalesQuotation } from "../../models/Quotation"
import { IAuth } from "../../store/Auth"
import HttpHandler from "../../utils/http-handler"
import { message } from "../../utils/message"
import { quotationStatusTone } from "../../utils/quotation-status"

export function ReceivedQuotationDetail() {
  const { t } = useTranslation(["quotation", "common"])
  const { id } = useParams<{ id: string }>()
  const { accessToken } = useSelector((state: { auth: IAuth }) => state.auth)

  const navigate = useNavigate()
  const GET = usePermission("PRICING_QUOTATION_GET")
  const ACCEPT = usePermission("PRICING_QUOTATION_ACCEPT")
  const REJECT = usePermission("PRICING_QUOTATION_REJECT")
  const [actionLoading, setActionLoading] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [acceptOpen, setAcceptOpen] = useState(false)

  const { data, isLoading, mutate } = useSWR<ISalesQuotation>(
    id && GET.hasAccess ? `swr.received-quotation.${id}` : null,
    () => ReceivedQuotationApi.get(id as string),
    { revalidateOnFocus: false },
  )

  const handleAccept = async () => {
    setActionLoading(true)
    try {
      await ReceivedQuotationApi.accept(id as string)
      message.success(t("detail.acceptSuccess"))
      setAcceptOpen(false)
      mutate()
    } catch (error) {
      message.error((error as HttpHandler)?.message || t("common:messages.error"))
    } finally {
      setActionLoading(false)
    }
  }

  const handleReject = async (reason: string) => {
    setActionLoading(true)
    try {
      await ReceivedQuotationApi.reject(id as string, { reason })
      message.success(t("detail.rejectSuccess"))
      setRejectOpen(false)
      mutate()
    } catch (error) {
      message.error((error as HttpHandler)?.message || t("common:messages.error"))
    } finally {
      setActionLoading(false)
    }
  }

  const handlePdf = async () => {
    try {
      const res = await fetch(ReceivedQuotationApi.pdfUrl(id as string), {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!res.ok) throw new Error(t("detail.pdfError"))
      const blob = await res.blob()
      window.open(URL.createObjectURL(blob), "_blank")
    } catch (error) {
      message.error((error as Error)?.message || t("detail.pdfError"))
    }
  }

  if (!GET.hasAccess) {
    return (
      <Box>
        <PageHeader title={t("list.title")} backPath="/received-quotation" />
        <DeniedCard permission="PRICING_QUOTATION_GET" title={t("list.title")} />
      </Box>
    )
  }

  if (isLoading) {
    return <Flex justify="center" py="xl"><Loader color="brand" /></Flex>
  }

  if (!data) {
    return (
      <Box>
        <PageHeader
          title={t("list.title")}
          subtitle={t("common:notFound")}
          backPath="/received-quotation"
        />
      </Box>
    )
  }

  const isSent = data.status === "SENT"
  const noPerm = t("common:permission.required")
  const notSent = t("detail.notSentReason")

  const actions: QuotationAction[] = [
    {
      key: "accept",
      label: t("detail.acceptButton"),
      allowed: isSent && ACCEPT.hasAccess,
      reason: !ACCEPT.hasAccess ? noPerm : isSent ? t("detail.acceptReasonOk") : notSent,
      onClick: () => isSent && ACCEPT.hasAccess && setAcceptOpen(true),
      primary: true,
      loading: actionLoading,
    },
    {
      key: "reject",
      label: t("detail.rejectButton"),
      allowed: isSent && REJECT.hasAccess,
      reason: !REJECT.hasAccess ? noPerm : isSent ? t("detail.rejectReasonOk") : notSent,
      onClick: () => isSent && REJECT.hasAccess && setRejectOpen(true),
      loading: actionLoading,
    },
    {
      key: "pdf",
      label: t("detail.pdfButton"),
      allowed: true,
      reason: t("detail.pdfReason"),
      onClick: handlePdf,
    },
  ]

  return (
    <Box>
      <Stack gap="md" className={classes.page}>
        <Group gap="md" wrap="wrap">
          <Button variant="default" leftSection={<CaretLeftIcon size={16} />} onClick={() => navigate("/received-quotation")}>
            {t("common:actions.back")}
          </Button>
          <Text className={classes.code} translate="no">{data.refCode || "-"}</Text>
          <DotPill tone={quotationStatusTone(data.status)}>{t(`status.${data.status}`)}</DotPill>
        </Group>

        <QuotationDetailView data={data} actions={actions} onCommentsSaved={mutate} />
      </Stack>

      <Modal opened={acceptOpen} onClose={() => setAcceptOpen(false)} centered size="md" radius={14} padding={24} withCloseButton={false}>
        <ConfirmDialogBody
          tone="success"
          question={t("detail.acceptConfirmTitle")}
          description={t("detail.acceptConfirmDesc")}
          onCancel={() => setAcceptOpen(false)}
          onConfirm={handleAccept}
          confirmColor="brand"
          confirmLabel={t("detail.acceptButton")}
          cancelLabel={t("common:cancel")}
          loading={actionLoading}
        />
      </Modal>

      <RejectModal opened={rejectOpen} onClose={() => setRejectOpen(false)} onConfirm={handleReject} loading={actionLoading} />
    </Box>
  )
}
